import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { apiService } from '../services/api';
import { CreateEquipmentDto } from '../types';
import EquipmentForm from '../components/forms/EquipmentForm';
import { ArrowLeft } from 'lucide-react';

const CreateEquipmentPage: React.FC = () => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [pendingPhotoFile, setPendingPhotoFile] = useState<File | null>(null);
	const [isFormDirty, setIsFormDirty] = useState(false);
	const lastNavigateAttemptRef = useRef<string | null>(null);
	const lastPromptAtRef = useRef<number>(0);
	const suppressNavigationRef = useRef<boolean>(false);

	const createEquipmentMutation = useMutation({
		mutationFn: async (data: CreateEquipmentDto) => {
			const result = await apiService.createEquipment(data);
			return result;
		},
		onSuccess: async (response) => {
			if (response.success && response.data) {
				// Upload photo if one was selected
				if (pendingPhotoFile && response.data.id) {
					try {
						await apiService.uploadEquipmentPhoto(response.data.id, pendingPhotoFile);
						toast.success('Photo uploaded successfully!');
					} catch (error: any) {
						console.error('Error uploading photo:', error);
						toast.error('Equipment created but photo upload failed');
					}
				}
				
				queryClient.invalidateQueries({ queryKey: ['equipment'] });
				toast.success(`Equipment "${response.data.name}" created successfully!`);
				navigate(`/equipment/${response.data.id}`);
			} else {
				toast.error(`Error: ${response.message || 'Unknown error occurred'}`);
			}
		},
		onError: (error: any) => {
			console.error('Error creating equipment:', error);
			const errorMessage = error?.response?.data?.message ||
				error?.response?.data?.errors?.join(', ') ||
				error.message ||
				'Unknown error';
			toast.error(`Error creating equipment: ${errorMessage}`);
		}
	});

	const handleSubmit = (data: CreateEquipmentDto) => {
		suppressNavigationRef.current = true;
		createEquipmentMutation.mutate(data);
	};

	const handleBackClick = () => {
		if (!isFormDirty) {
			navigate('/equipment');
			return;
		}

		const now = Date.now();
		const targetPath = '/equipment';
		const samePath = lastNavigateAttemptRef.current === targetPath;
		const withinWindow = now - lastPromptAtRef.current < 5000;

		if (samePath && withinWindow) {
			// proceed with navigation
			lastNavigateAttemptRef.current = null;
			lastPromptAtRef.current = 0;
			suppressNavigationRef.current = true;
			navigate(targetPath);
			return;
		}

		lastNavigateAttemptRef.current = targetPath;
		lastPromptAtRef.current = now;
		toast(() => (
			<span className="text-sm">
				You have unsaved changes. Click the back button again to leave.
			</span>
		), { id: 'equipment-back-warning', duration: 4000, icon: '⚠️' });
	};

	return (
		<div className="p-4 space-y-6">
			{/* Header */}
			<div className="mb-2">
				<div className="mb-2">
					<h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Create Equipment</h1>
					<p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Add new equipment to track</p>
				</div>
				{/* Back to Equipment Link */}
				<button
					onClick={handleBackClick}
					className="flex items-center gap-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
				>
					<ArrowLeft className="w-4 h-4" />
					<span>Back to Equipment</span>
				</button>
			</div>

			<div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
				<EquipmentForm
					onSubmit={handleSubmit}
					isLoading={createEquipmentMutation.isPending}
					error={createEquipmentMutation.error ? 'Failed to create equipment' : undefined}
					onPendingPhotoChange={setPendingPhotoFile}
					onFormDirtyChange={setIsFormDirty}
				/>
			</div>
		</div>
	);
};

export default CreateEquipmentPage;

